import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Select,
  Typography,
} from 'antd';

import {
  SYNTHETIC_BOND_CURVE_TENOR_MONTHS,
  SYNTHETIC_BOND_FAMILY_METADATA_FIELDS,
  SYNTHETIC_BOND_ISSUANCE_TENORS,
  SyntheticBondGenerationDraft,
  SyntheticBondGenerationOptions,
} from 'state';
import CurveFamilyEditor from './CurveFamilyEditor';
import { InfoLabel } from '../BondsPlayground/ModelGuide';

type Props = {
  disabled?: boolean;
  seriesLocked?: boolean;
  draft: SyntheticBondGenerationDraft;
  options?: SyntheticBondGenerationOptions;
  previewing?: boolean;
  onCurrencyChange: (currencyId: number | null) => void;
  onDraftChange: (patch: Partial<SyntheticBondGenerationDraft>) => void;
  onMetadataChange: (
    field: keyof SyntheticBondGenerationDraft['family_metadata'],
    value: string | number | null,
  ) => void;
  onPreview: () => void;
};

export const isLegalSyntheticBondIssueDate = (
  value: string | null | undefined,
  schedule = { day: 21, months: [3, 6, 9, 12] },
) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = dayjs(value);
  return (
    parsed.isValid() &&
    parsed.format('YYYY-MM-DD') === value &&
    parsed.date() === schedule.day &&
    schedule.months.includes(parsed.month() + 1)
  );
};

export const syntheticBondDraftProblems = (
  draft: SyntheticBondGenerationDraft,
  options?: SyntheticBondGenerationOptions,
) => {
  const problems: string[] = [];
  const schedule = options?.issue_schedule || {
    day: 21,
    months: [3, 6, 9, 12],
  };
  const sources = SYNTHETIC_BOND_CURVE_TENOR_MONTHS.map(
    tenor => draft.source_asset_ids_by_tenor_months[String(tenor)],
  );

  if (!draft.currency_id) problems.push('Select one currency family.');
  if (!draft.series_id) problems.push('Save or select a Series first.');
  if (sources.some(id => id === null)) {
    problems.push('Assign one source UnderlyingAsset to every tenor slot.');
  }
  const selectedSources = sources.filter((id): id is number => id !== null);
  if (new Set(selectedSources).size !== selectedSources.length) {
    problems.push('Each tenor slot must use a distinct source asset.');
  }
  if (draft.initial_tenors.length !== 1)
    problems.push('Select exactly one Series initial tenor.');
  if (!isLegalSyntheticBondIssueDate(draft.first_issue_date, schedule)) {
    problems.push('Choose a legal first issue date.');
  }
  if (!isLegalSyntheticBondIssueDate(draft.last_issue_date, schedule)) {
    problems.push('Choose a legal last issue date.');
  }
  if (
    draft.first_issue_date &&
    draft.last_issue_date &&
    draft.first_issue_date > draft.last_issue_date
  ) {
    problems.push('First issue date must not be after last issue date.');
  }
  if (
    draft.first_issue_date &&
    dayjs(draft.first_issue_date).isAfter(dayjs(), 'day')
  ) {
    problems.push('Issue dates must not be in the future.');
  }
  if (
    draft.last_issue_date &&
    dayjs(draft.last_issue_date).isAfter(dayjs(), 'day')
  ) {
    problems.push('Issue dates must not be in the future.');
  }

  const supportedMetadata = new Set<string>(
    SYNTHETIC_BOND_FAMILY_METADATA_FIELDS,
  );
  const requiredMetadata = (
    options?.family_metadata_schema?.required_fields || [
      'geography',
      'region',
      'market_hours_id',
      'issuer_type',
      'country_code',
    ]
  ).filter(field => supportedMetadata.has(field));
  const missingMetadata = requiredMetadata.filter(field => {
    const value = draft.family_metadata[field];
    return value === null || value === undefined || String(value).trim() === '';
  });
  if (missingMetadata.length) {
    problems.push(
      `Complete required family metadata: ${missingMetadata.join(', ')}.`,
    );
  }
  return problems;
};

const BatchIssuerForm: React.FC<Props> = ({
  disabled,
  draft,
  onCurrencyChange,
  onDraftChange,
  onMetadataChange,
  onPreview,
  options,
  previewing,
  seriesLocked,
}) => {
  const schedule = options?.issue_schedule || {
    day: 21,
    months: [3, 6, 9, 12],
  };
  const problems = useMemo(
    () => syntheticBondDraftProblems(draft, options),
    [draft, options],
  );
  const disabledDate = (date: Dayjs) =>
    date.isAfter(dayjs(), 'day') ||
    date.date() !== schedule.day ||
    !schedule.months.includes(date.month() + 1);
  const dateValue = (value: string | null) =>
    value && dayjs(value).isValid() ? dayjs(value) : null;
  const targets = options?.targets || [
    { key: 'AFS_BOND', label: 'AFS Bond', enabled: true },
  ];

  return (
    <section className="synthetic-bonds__issuer-form">
      <div className="synthetic-bonds__section-title">
        <div>
          <Typography.Title level={4}>
            2. Review Series defaults and issuance dates
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            One Series is generated per batch. Every date edit invalidates a
            prior preview, so Create can only publish exactly what was reviewed.
          </Typography.Paragraph>
        </div>
      </div>

      <CurveFamilyEditor
        disabled={disabled || seriesLocked}
        draft={draft}
        onCurrencyChange={onCurrencyChange}
        onDraftChange={onDraftChange}
        onMetadataChange={onMetadataChange}
        options={options}
      />

      <div className="synthetic-bonds__form-grid synthetic-bonds__form-grid--program">
        <label className="synthetic-bonds__field">
          <Typography.Text strong>
            <InfoLabel label="Issuance tenors" title="Generated asset tenors">
              The Series initial tenor is applied to every quarterly legal date
              in the inclusive period.
            </InfoLabel>
          </Typography.Text>
          <Select<number>
            disabled={disabled || seriesLocked}
            options={(
              options?.issuance_tenors || [...SYNTHETIC_BOND_ISSUANCE_TENORS]
            ).map(tenor => ({
              value: tenor,
              label: `${tenor} years`,
            }))}
            placeholder="Select tenors"
            value={draft.initial_tenors[0]}
            onChange={initialTenor =>
              onDraftChange({ initial_tenors: [initialTenor] })
            }
          />
        </label>

        <label className="synthetic-bonds__field">
          <Typography.Text strong>First issue date</Typography.Text>
          <DatePicker
            disabled={disabled}
            disabledDate={disabledDate}
            format="YYYY-MM-DD"
            inputReadOnly={false}
            placeholder="21 Mar / Jun / Sep / Dec"
            value={dateValue(draft.first_issue_date)}
            onChange={(_, value) =>
              onDraftChange({
                first_issue_date:
                  typeof value === 'string' && value ? value : null,
              })
            }
          />
        </label>

        <label className="synthetic-bonds__field">
          <Typography.Text strong>Last issue date</Typography.Text>
          <DatePicker
            disabled={disabled}
            disabledDate={disabledDate}
            format="YYYY-MM-DD"
            inputReadOnly={false}
            placeholder="Required inclusive bound"
            value={dateValue(draft.last_issue_date)}
            onChange={(_, value) =>
              onDraftChange({
                last_issue_date:
                  typeof value === 'string' && value ? value : null,
              })
            }
          />
        </label>

        <label className="synthetic-bonds__field">
          <Typography.Text strong>Asset class target</Typography.Text>
          <Select
            disabled={disabled}
            options={targets.map(target => ({
              value: target.key,
              label: target.label,
              disabled: !target.enabled,
              title: target.reason,
            }))}
            value={draft.target_kind}
            onChange={target_kind => onDraftChange({ target_kind })}
          />
        </label>
      </div>

      <Descriptions
        bordered
        className="synthetic-bonds__policy-summary"
        column={{ xs: 1, sm: 2, lg: 4 }}
        size="small"
      >
        <Descriptions.Item label="Face">100</Descriptions.Item>
        <Descriptions.Item label="Coupon frequency">
          Semiannual
        </Descriptions.Item>
        <Descriptions.Item label="Issue fixing">
          Latest valid as-of EOD
        </Descriptions.Item>
        <Descriptions.Item label="Curve freshness">
          Carried forward; no configured staleness rejection
        </Descriptions.Item>
        <Descriptions.Item label="Pricing horizon">
          Stops below 6M or at final system data
        </Descriptions.Item>
        <Descriptions.Item label="Published history">
          Clean bid and ask only
        </Descriptions.Item>
      </Descriptions>

      {problems.length > 0 && (
        <Alert
          className="synthetic-bonds__validation-summary"
          description={problems.join(' ')}
          message="Complete the family before preview"
          showIcon
          type="info"
        />
      )}

      <div className="synthetic-bonds__form-actions">
        <Button
          disabled={disabled || problems.length > 0}
          loading={previewing}
          onClick={onPreview}
          type="primary"
        >
          Preview creation
        </Button>
        <Typography.Text type="secondary">
          Preview creates review records only—no bond or MarketHistory data.
        </Typography.Text>
      </div>
    </section>
  );
};

export default BatchIssuerForm;
